import type { FC, ReactNode } from 'react'
import type { ArticleSummary, Topic } from '@/types'

import Link from 'next/link'
import { Reveal } from '@/components/motion/reveal'
import { SectionHeading } from '@/components/site/section-heading'

interface JournalTopicsProps {
    topics: Topic[]
    articles: ArticleSummary[]
}

export const JournalTopics: FC<JournalTopicsProps> = ({ topics, articles }): ReactNode => {
    const groups = topics
        .map((topic) => ({ topic, items: articles.filter((article) => article.topic?.id === topic.id) }))
        .filter((group) => group.items.length > 0)

    if (groups.length === 0) return null

    return (
        <section
            id='topics'
            className='relative z-[2] flex w-full flex-col gap-10 overflow-clip bg-background px-4 pb-section-lg lg:gap-12 lg:px-6'
        >
            <SectionHeading lead="Read by" title="subject" align='right' />

            <ul className='flex w-full flex-col border-t border-border'>
                {groups.map(({ topic, items }, index) => (
                    <li key={topic.id} className='border-b border-border'>
                        <Reveal y={20} delay={index * 0.05}>
                            <div className='grid grid-cols-1 gap-5 py-8 text-foreground md:grid-cols-2 md:gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,0.8fr)_minmax(0,1fr)] lg:py-10'>
                                <div className='flex items-baseline gap-3'>
                                    <span className='t-number shrink-0 text-muted-foreground'>
                                        {`(${items.length})`}
                                    </span>

                                    <h3
                                        data-mdlfy-cms={`topics:${topic.id}:name`}
                                        className='t-name text-left'
                                    >
                                        {topic.name}
                                    </h3>
                                </div>

                                {topic.description && (
                                    <p
                                        data-mdlfy-cms={`topics:${topic.id}:description`}
                                        className='t-body max-w-[460px] text-left text-muted-foreground lg:pt-2'
                                    >
                                        {topic.description}
                                    </p>
                                )}

                                <ul className='flex flex-col gap-3 md:col-span-2 lg:col-span-1 lg:pt-2'>
                                    {items.map((article) => (
                                        <li key={article.id}>
                                            <Link
                                                href={`/journal/${article.slug}`}
                                                className='group/topic inline-flex items-baseline gap-3 text-left'
                                            >
                                                <span aria-hidden='true' className='t-meta text-muted-foreground transition-transform duration-base ease-studio group-hover/topic:translate-x-1'>
                                                    &rarr;
                                                </span>

                                                <span
                                                    data-mdlfy-cms={`articles:${article.id}:title`}
                                                    className='t-label underline decoration-transparent decoration-1 underline-offset-4 group-hover/topic:decoration-current'
                                                >
                                                    {article.title}
                                                </span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                    </li>
                ))}
            </ul>
        </section>
    )
}
